<template>
  <RygenTabs
    v-model:active-tab-key="internalActiveTabKey"
    :tabs="tabs"
    class="w-full"
  >
    <TabPanel value="CHARGES">
      <ChargesTab :rate-quote="rateQuote" :email-tender="emailTender" />
    </TabPanel>

    <TabPanel value="RAW_CHARGES">
      <RawChargesRedesignTab
        v-if="arApFeatureFlagEnabled"
        :rate-quote="rateQuote"
        :email-tender="emailTender"
      />
      <RawChargesTab
        v-else
        :rate-quote="rateQuote"
        :email-tender="emailTender"
      />
    </TabPanel>

    <TabPanel value="CARRIER_DETAILS">
      <CarrierDetailsTab :contact="contact.data" />
    </TabPanel>

    <TabPanel value="OTHER_DETAILS">
      <OtherDetailsTab :rate-quote="rateQuote" />
    </TabPanel>
  </RygenTabs>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { usePrimeVue } from 'primevue/config';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Tab } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Contact, RateQuote } from '@/model';

import CarrierDetailsTab from '@/components/rateShop/expandedDetails/CarrierDetailsTab.vue';
import ChargesTab from '@/components/rateShop/expandedDetails/ChargesTab.vue';
import OtherDetailsTab from '@/components/rateShop/expandedDetails/OtherDetailsTab.vue';
import RawChargesRedesignTab from '@/components/rateShop/expandedDetails/RawChargesRedesignTab.vue';
import RawChargesTab from '@/components/rateShop/expandedDetails/RawChargesTab.vue';
import RygenTabs from '@rygen/rygen-ui/components/ui/RygenTabs.vue';
import TabPanel from 'primevue/tabpanel';

export type TabKey =
  'CHARGES' | 'RAW_CHARGES' | 'OTHER_DETAILS' | 'CARRIER_DETAILS';

const props = defineProps({
  activeTabKey: {
    type: String as PropType<TabKey>,
    default: () => 'CHARGES',
  },
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  emailTender: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, emailTender, activeTabKey } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:active-tab-key', newVal: TabKey): void;
}>();

const arApFeatureFlagEnabled = useFlag('ar-ap-split-feat-flag');
const { t } = useI18n();
const toast = useToast();

const internalActiveTabKey: Ref<TabKey> = ref(activeTabKey.value);
const contact: Ref<Loadable<Contact>> = ref({ loading: false });

const hasPrimaryContact = computed(() => {
  return !!contact.value.data;
});

const isErrored = computed(() => rateQuote.value.rateStatus === 'ERROR');

const tabs = computed((): Tab<TabKey>[] => [
  {
    key: 'CHARGES',
    header: t('rateShop.charges.charges', 2),
    // an errored quote has no charges to show, so it opens straight to Other Details
    visible: !isErrored.value,
  },
  {
    key: 'RAW_CHARGES',
    header: t('rateShop.details.rawCharges'),
    visible: rateQuote.value.hasAnyUplift && !isErrored.value,
  },
  {
    key: 'CARRIER_DETAILS',
    header: t('common.paramDetails', { param: t('carriers.carriers') }),
    visible: hasPrimaryContact.value,
  },
  {
    key: 'OTHER_DETAILS',
    header: t('rateShop.otherDetails'),
  },
]);

const loadCarrierContact = async (carrierId: number) => {
  try {
    contact.value.loading = true;
    contact.value.data = await api.carrier.findPrimaryContact(carrierId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // Can be a previous value if user removes carrier and tenders another
    contact.value.data = undefined;

    console.error('Error loading carrier contact', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('common.paramDetails', { param: t('carriers.carriers') }),
      }),
      error: e,
    });
  } finally {
    contact.value.loading = false;
  }
};

watch(
  () => rateQuote.value.carrier?.id,
  (newVal: number | undefined) => {
    if (newVal) {
      loadCarrierContact(newVal);
    }
  },
  { immediate: true },
);

watch(
  activeTabKey,
  (newVal: TabKey) => {
    internalActiveTabKey.value = newVal;
  },
  { immediate: true },
);

watch(internalActiveTabKey, (newVal: TabKey) => {
  emit('update:active-tab-key', newVal);
});

onMounted(() => {
  internalActiveTabKey.value = isErrored.value ? 'OTHER_DETAILS' : 'CHARGES';
});

defineExpose({
  $primevue: usePrimeVue(),
});
</script>
